import React, { type JSX } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import ParagraphContent from "./YamlParagraphContent";
import { previewText, type ParagraphItemProps } from "./yamlPreviewTypes";

const TYPE_COLOURS: Record<string, string> = {
  markdown: "bg-blue-100 text-blue-700",
  recipients: "bg-purple-100 text-purple-700",
  signatures: "bg-green-100 text-green-700",
  "advisors-table": "bg-orange-100 text-orange-700",
  "contacts-table": "bg-orange-100 text-orange-700",
};

/** Paragraph type chip, colour-coded per known type. */
function TypeBadge({ type }: { type: string }): JSX.Element {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_COLOURS[type] || "bg-gray-100 text-gray-700"}`}
    >
      {type}
    </span>
  );
}

/** Style chip for paragraphs that declare one. */
function StyleBadge({ style }: { style: string }): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
      {style}
    </span>
  );
}

/** The badge row identifying a paragraph. */
function ParagraphBadges({
  index,
  paragraphType,
  style,
  section,
  fixed,
}: {
  index: number;
  paragraphType: string;
  style?: string;
  section?: string;
  fixed?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center space-x-2 mb-1">
      <span className="text-xs font-medium text-gray-500">#{index}</span>
      <TypeBadge type={paragraphType} />
      {style && <StyleBadge style={style} />}
      {section && <span className="text-xs text-gray-400">• {section}</span>}
      {fixed && (
        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">fixed</span>
      )}
    </div>
  );
}

/** Collapsible paragraph row; expanded rows render their full content. */
export default function ParagraphItem({
  paragraph,
  index,
  isExpanded,
  onToggle,
}: ParagraphItemProps): JSX.Element {
  const { type, data, style, section, fixed } = paragraph;
  const paragraphType = type || "markdown";

  return (
    <div className="hover:bg-gray-50 transition-colors">
      <div onClick={onToggle} className="w-full flex items-start justify-between p-4 cursor-pointer">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <ParagraphBadges
              index={index}
              paragraphType={paragraphType}
              style={style}
              section={section}
              fixed={fixed}
            />
            {!isExpanded && (
              <div className="text-sm text-gray-600 truncate">
                {previewText(paragraphType, data)}
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pl-12">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <ParagraphContent paragraph={paragraph} />
          </div>
        </div>
      )}
    </div>
  );
}
